/**
 * Theme module — mirrors the site's `starlight-theme` localStorage contract
 * so free.nowline.io, nowline.io, and docs.nowline.io all stay in sync.
 *
 * Contract (per Starlight convention):
 *   - localStorage['starlight-theme'] === '' → auto (follow system)
 *   - localStorage['starlight-theme'] === 'light' → explicit light
 *   - localStorage['starlight-theme'] === 'dark'  → explicit dark
 *
 * The resolved theme (never 'auto') is written to
 * document.documentElement.dataset.theme so CSS `[data-theme="dark"]` works.
 */

export type Theme = 'auto' | 'light' | 'dark';

export const THEME_STORAGE_KEY = 'starlight-theme';

export function parseTheme(value: unknown): Theme {
    if (value === 'light' || value === 'dark') return value;
    return 'auto';
}

export function getStoredTheme(): Theme {
    try {
        return parseTheme(localStorage.getItem(THEME_STORAGE_KEY));
    } catch {
        return 'auto';
    }
}

export function storeTheme(theme: Theme): void {
    try {
        // Starlight stores '' for auto so the pre-paint script falls back to matchMedia.
        localStorage.setItem(THEME_STORAGE_KEY, theme === 'auto' ? '' : theme);
    } catch {
        // storage unavailable
    }
}

export function getSystemTheme(): 'light' | 'dark' {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Resolve 'auto' to the system value; 'light'/'dark' pass through. */
export function resolveTheme(theme: Theme): 'light' | 'dark' {
    return theme === 'auto' ? getSystemTheme() : theme;
}

/** Apply a theme choice: write to localStorage and set dataset.theme. */
export function applyTheme(theme: Theme): void {
    storeTheme(theme);
    document.documentElement.dataset.theme = resolveTheme(theme);
}

/**
 * One-time init: resolve and apply the stored theme synchronously.
 * Called from the pre-paint inline script in index.html.
 * Safe to call again from JS — idempotent.
 */
export function initTheme(): void {
    applyTheme(getStoredTheme());
}
